/**
 * Categories that exist in the strain data but not in WooCommerce.
 *
 * The shop has ten product categories and CBD is not among them — verified
 * against the live store. But three strains are CBD-rich, and the strains site
 * (seeds.dutchpassion.co.th) already lists them under `cbd-seeds`, so the gap is
 * in WooCommerce's taxonomy rather than in the catalogue itself.
 *
 * Rather than link customers off-site or leave a dead entry, these are derived
 * from strain metadata and rendered as ordinary category pages.
 *
 * If the category is later created in WooCommerce, delete the entry here — the
 * real one takes precedence and this would become a duplicate.
 */

import type { Product } from '$lib/services/products';
import type { Locale } from '$lib/i18n';

export interface DerivedCategory {
	slug: string;
	/** One entry per locale — every locale renders this category page. */
	label: Record<Locale, string>;
	description: Record<Locale, string>;
	/** Decides membership from strain metadata. */
	includes: (product: Product) => boolean;
}

const attribute = (product: Product, key: string) =>
	product.attributes?.find((a) => a.key === key)?.value;

export const DERIVED_CATEGORIES: DerivedCategory[] = [
	{
		slug: 'cbd-seeds',
		label: {
			en: 'CBD Seeds',
			th: 'เมล็ดพันธุ์ CBD',
			ja: '高CBDシード',
			fr: 'Graines riches en CBD',
			es: 'Semillas ricas en CBD',
			ru: 'Семена с высоким CBD',
			zh: '高CBD种子',
			vi: 'Hạt giống giàu CBD'
		},
		description: {
			en: 'CBD-rich cannabis seeds with high CBD levels and THC kept low — bred for growers who want the therapeutic profile without a strong high.',
			th: 'เมล็ดพันธุ์กัญชาที่อุดมด้วย CBD มีระดับ CBD สูงและ THC ต่ำ เหมาะสำหรับผู้ปลูกที่ต้องการคุณสมบัติทางการแพทย์',
			ja: 'CBD含有量が高くTHCを低く抑えたカンナビスシード。強い酩酊感なしに療法的な特性を求める栽培者向けに育種されています。',
			fr: 'Graines de cannabis riches en CBD, à taux de THC volontairement bas — sélectionnées pour les cultivateurs qui recherchent le profil thérapeutique sans effet planant marqué.',
			es: 'Semillas de cannabis ricas en CBD, con niveles de THC deliberadamente bajos — seleccionadas para cultivadores que buscan el perfil terapéutico sin un efecto psicoactivo fuerte.',
			ru: 'Семена конопли с высоким содержанием CBD и намеренно низким THC — выведены для тех, кому нужен терапевтический профиль без сильного психоактивного эффекта.',
			zh: '富含 CBD、THC 含量刻意保持较低的大麻种子——专为希望获得疗愈特性而不要强烈迷幻感的种植者培育。',
			vi: 'Hạt giống cần sa giàu CBD, hàm lượng THC được giữ thấp có chủ đích — lai tạo cho người trồng muốn đặc tính trị liệu mà không bị phê mạnh.'
		},
		// The `CBD Rich` family is the curated signal; a declared CBD percentage
		// catches anything added later without the family set.
		includes: (product) =>
			attribute(product, 'family') === 'CBD Rich' || Boolean(attribute(product, 'cbd'))
	}
];

export const findDerivedCategory = (slug: string) =>
	DERIVED_CATEGORIES.find((category) => category.slug === decodeURIComponent(slug));

/**
 * Display names for WooCommerce categories, keyed by English slug then locale.
 *
 * Two different jobs, deliberately in one table:
 *
 * 1. **Overrides for `en`/`th`,** where the snapshot already has a name but it
 *    is not the one we want to show. Only `fast-flowering-cannabis-seeds`
 *    qualifies. The label otherwise comes from `category.name` in the WordPress
 *    snapshot. That snapshot is now hand-maintained and reconciled against the
 *    live store, so a name corrected there is a name someone has to remember to
 *    keep corrected. Overriding here does not depend on anyone remembering.
 * 2. **Names for `ja`/`fr`/`es`/`ru`/`zh`/`vi`,** which have no snapshot of
 *    their own. `commerceLocale()` resolves them to the English catalogue, so
 *    without an entry here every category heading, `<title>` and nav link on
 *    those six locales rendered in English on an otherwise translated page.
 *
 * The wording matches the category cards already written into
 * `pages/home/Home.<locale>.svelte`, so the menu and the homepage agree.
 *
 * **Slugs are deliberately untouched.** `fast-flowering-cannabis-seeds` is a
 * live, indexed URL; only what visitors read changes.
 */
export const CATEGORY_LABELS: Record<string, Partial<Record<Locale, string>>> = {
	'cannabis-seeds': {
		ja: 'カンナビスシード',
		fr: 'Graines de cannabis',
		es: 'Semillas de cannabis',
		ru: 'Семена конопли',
		zh: '大麻种子',
		vi: 'Hạt giống cần sa'
	},
	'feminised-seeds': {
		ja: 'フェミナイズドシード',
		fr: 'Graines féminisées',
		es: 'Semillas feminizadas',
		ru: 'Феминизированные семена',
		zh: '女性化种子',
		vi: 'Hạt giống nữ hóa'
	},
	'autoflower-seeds': {
		ja: 'オートフラワーシード',
		fr: 'Graines à autofloraison',
		es: 'Semillas autoflorecientes',
		ru: 'Автоцветущие семена',
		zh: '自动开花种子',
		vi: 'Hạt giống tự ra hoa'
	},
	'fast-flowering-cannabis-seeds': {
		en: 'Fast flowering seeds',
		th: 'เมล็ดพันธุ์ออกดอกเร็ว',
		ja: '早期開花シード',
		fr: 'Graines à floraison rapide',
		es: 'Semillas de floración rápida',
		ru: 'Быстроцветущие семена',
		zh: '快速开花种子',
		vi: 'Hạt giống ra hoa nhanh'
	},
	'high-thc-seeds': {
		ja: '高THCシード',
		fr: 'Graines à THC élevé',
		es: 'Semillas con THC alto',
		ru: 'Семена с высоким THC',
		zh: '高THC种子',
		vi: 'Hạt giống THC cao'
	},
	'indoor-seeds': {
		ja: '屋内栽培シード',
		fr: 'Graines pour intérieur',
		es: 'Semillas para interior',
		ru: 'Семена для выращивания в помещении',
		zh: '室内种植种子',
		vi: 'Hạt giống trồng trong nhà'
	},
	// "Outdoor Thailand", not "Outdoor Seeds" — the shop names this category for
	// the climate it is selected for, and the translations keep that.
	'outdoor-seeds': {
		ja: '屋外栽培タイ',
		fr: 'Extérieur Thaïlande',
		es: 'Exterior Tailandia',
		ru: 'Открытый грунт Таиланд',
		zh: '户外泰国',
		vi: 'Ngoài trời Thái Lan'
	},
	'indica-seeds': {
		ja: 'インディカシード',
		fr: 'Graines indica',
		es: 'Semillas indica',
		ru: 'Семена индика',
		zh: '印度大麻种子',
		vi: 'Hạt giống Indica'
	},
	'sativa-seeds': {
		ja: 'サティバシード',
		fr: 'Graines sativa',
		es: 'Semillas sativa',
		ru: 'Семена сатива',
		zh: '苜蓿大麻种子',
		vi: 'Hạt giống Sativa'
	},
	'hybrid-seeds': {
		ja: 'ハイブリッドシード',
		fr: 'Graines hybrides',
		es: 'Semillas híbridas',
		ru: 'Гибридные семена',
		zh: '混种种子',
		vi: 'Hạt giống lai'
	},
	'cbd-seeds': {
		ja: '高CBDシード',
		fr: 'Graines riches en CBD',
		es: 'Semillas ricas en CBD',
		ru: 'Семена с высоким CBD',
		zh: '高CBD种子',
		vi: 'Hạt giống giàu CBD'
	}
};

/**
 * The label to display for a category, given the locale-stable English slug.
 *
 * Falls through to the snapshot name when this locale has no entry, so `en` and
 * `th` keep reading their own WooCommerce data and an unlisted category behaves
 * exactly as before.
 */
export function categoryLabel(englishSlug: string, fallback: string, locale: string): string {
	return CATEGORY_LABELS[decodeURIComponent(englishSlug)]?.[locale as Locale] ?? fallback;
}
